import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { ReduxStore, ReduxNext } from '@makemydeal/dr-shared-store';
import { sessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { Actions, DealXgSocketActions } from '@makemydeal/dr-sales-checkin-store';
import { CHANGE_VEHICLE_CLICKED } from '@makemydeal/dr-activities-change-vehicle';

const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';
const VEHICLE_SELECTED_MILESTONE = 'vehicle-selected';

// --- User Journey Milestones ---
const MILESTONE_MAP: Record<string, string> = {
    BOOTSTRAP_SUCCESS: 'app-loaded',
    [Actions.SHOW_CUSTOMER_VEHICLES_SLIDEOUT]: 'deals-loaded',
    CHECKIN_SUCCESS: 'check-in-completed',
    OPEN_TRADE_IN: 'trade-in-started',
    TRADE_IN_UPDATE_SUCCESS: 'trade-in-completed',
    CREDIT_APP_SUBMITTED: 'credit-app-submitted',
    INITIALIZING_ROLE_ASSUMPTION: 'role-assumption-resolved',
    PREPARE_DEAL_SETUP_STATE: 'deal-setup-started',
    CONTINUE_TO_APPLY_DEAL: 'deal-applied',
    SET_SELECTED_CUSTOMER: 'customer-selected',
    FETCH_DEAL_REQUEST: 'deal-opened',
    OPEN_DEAL_FROM_CUSTOMER_VEHICLES: 'deal-from-vehicles',
    // Business KPI milestones
    '[CustomerCard] Apply Succeeded': 'customer-details-captured',
    DDJ_OPENED: 'ddj-opened',
    REQUEST_PROPOSAL_SEND_REQUEST_CLICKED: 'deal-structure-requested',
    REQUEST_PROPOSAL_ACCEPT_DEAL_CLICKED: 'deal-accepted',
    CHANGE_VEHICLE_CLICKED: 'vehicle-switched',
    [DealXgSocketActions.REFRESH_DEAL_REQUESTED]: 'deal-refresh-accepted',
    FETCH_DEAL_REF_ID_SUCCESS: 'ddj-opened',
    [Actions.SEND_CREDIT_APP_LINK_CDL]: 'credit-app-shared',
    [Actions.SEND_DOC_UPLOAD_LINK_CDL]: 'doc-upload-shared',
    [Actions.SEND_DEAL_PDF_LINK_CDL]: 'deal-pdf-shared',
    [Actions.CREDIT_CONSENT_CLICKED]: 'credit-consent-clicked'
};

// --- Integration Point Events (API calls, external services) ---
const INTEGRATION_EVENT_MAP: Record<string, { event: string; category: string; outcome: 'request' | 'success' | 'failure' }> = {
    // Deal Operations
    UPDATE_DEAL_REQUEST: { event: 'deal-save', category: 'deal', outcome: 'request' },
    UPDATE_DEAL_SUCCESS: { event: 'deal-save', category: 'deal', outcome: 'success' },
    UPDATE_DEAL_FAILURE: { event: 'deal-save', category: 'deal', outcome: 'failure' },
    FETCH_DEAL_REQUEST: { event: 'deal-fetch', category: 'deal', outcome: 'request' },
    FETCH_DEAL_SUCCESS: { event: 'deal-fetch', category: 'deal', outcome: 'success' },
    FETCH_DEAL_FAILURE: { event: 'deal-fetch', category: 'deal', outcome: 'failure' },
    CANCEL_DEAL: { event: 'deal-cancel', category: 'deal', outcome: 'request' },

    // Deep Links / Share
    ADD_DEEPLINK_URL_REQUEST: { event: 'deep-link-fetch', category: 'share', outcome: 'request' },
    ADD_DEEPLINK_URL_SUCCESS: { event: 'deep-link-fetch', category: 'share', outcome: 'success' },
    ADD_DEEPLINK_URL_FAILURE: { event: 'deep-link-fetch', category: 'share', outcome: 'failure' },
    SEND_CREDIT_APP_LINK_CDL: { event: 'share-credit-app-link', category: 'share', outcome: 'request' },
    SEND_DOC_UPLOAD_LINK_CDL: { event: 'share-doc-upload-link', category: 'share', outcome: 'request' },

    // Customer Card / Inbox
    SET_CUSTOMER_CARD_STATE: { event: 'customer-card-opened', category: 'inbox', outcome: 'request' },

    // Trade-In
    TRADE_IN_UPDATE_REQUEST: { event: 'trade-in-update', category: 'trade-in', outcome: 'request' },
    TRADE_IN_UPDATE_SUCCESS: { event: 'trade-in-update', category: 'trade-in', outcome: 'success' },
    TRADE_IN_UPDATE_FAILURE: { event: 'trade-in-update', category: 'trade-in', outcome: 'failure' },
    TRADE_IN_SEARCH_REQUEST: { event: 'trade-in-search', category: 'trade-in', outcome: 'request' },
    TRADE_IN_SEARCH_SUCCESS: { event: 'trade-in-search', category: 'trade-in', outcome: 'success' },
    TRADE_IN_SEARCH_FAILURE: { event: 'trade-in-search', category: 'trade-in', outcome: 'failure' },
    TRADE_ESTIMATE_REQUEST: { event: 'trade-in-estimate', category: 'trade-in', outcome: 'request' },
    TRADE_ESTIMATE_SUCCESS: { event: 'trade-in-estimate', category: 'trade-in', outcome: 'success' },
    TRADE_ESTIMATE_FAILURE: { event: 'trade-in-estimate', category: 'trade-in', outcome: 'failure' },

    // Credit App
    CREDIT_APP_REQUEST: { event: 'credit-app-submit', category: 'credit-app', outcome: 'request' },
    CREDIT_APP_SUBMITTED: { event: 'credit-app-submit', category: 'credit-app', outcome: 'success' },
    CREDIT_APP_FAILURE: { event: 'credit-app-submit', category: 'credit-app', outcome: 'failure' },

    // Vehicle Selection
    BUY_THIS_VEHICLE: { event: 'vehicle-selected', category: 'vehicle', outcome: 'success' },
    CHANGE_VEHICLE_REQUEST: { event: 'vehicle-change', category: 'vehicle', outcome: 'request' },
    CHANGE_VEHICLE_SUCCESS: { event: 'vehicle-change', category: 'vehicle', outcome: 'success' },
    CHANGE_VEHICLE_FAILURE: { event: 'vehicle-change', category: 'vehicle', outcome: 'failure' },

    // Sync Customer
    SYNC_CUSTOMER_REQUEST: { event: 'sync-customer', category: 'customer', outcome: 'request' },
    SYNC_CUSTOMER_SUCCESS: { event: 'sync-customer', category: 'customer', outcome: 'success' },
    SYNC_CUSTOMER_FAILURE: { event: 'sync-customer', category: 'customer', outcome: 'failure' },

    // Save Participants
    SAVE_PARTICIPANTS_REQUEST: { event: 'save-participants', category: 'customer', outcome: 'request' },
    SAVE_PARTICIPANTS_SUCCESS: { event: 'save-participants', category: 'customer', outcome: 'success' },
    SAVE_PARTICIPANTS_FAILURE: { event: 'save-participants', category: 'customer', outcome: 'failure' },

    // Deal Refresh (WebSocket)
    DEAL_REFRESH_RECEIVED: { event: 'deal-refresh-websocket', category: 'websocket', outcome: 'success' },
    DEAL_REFRESH_MODAL_OPENED: { event: 'deal-refresh-modal-shown', category: 'websocket', outcome: 'request' },
    DEAL_REFRESH_ACCEPTED: { event: 'deal-refresh-accepted', category: 'websocket', outcome: 'success' },
    DEAL_REFRESH_DISMISSED: { event: 'deal-refresh-dismissed', category: 'websocket', outcome: 'failure' },

    // Request Proposal
    REQUEST_PROPOSAL_SUBMIT: { event: 'request-proposal', category: 'deal', outcome: 'request' },
    REQUEST_PROPOSAL_SUCCESS: { event: 'request-proposal', category: 'deal', outcome: 'success' },
    REQUEST_PROPOSAL_FAILURE: { event: 'request-proposal', category: 'deal', outcome: 'failure' },

    // Deal Structure
    GET_DEAL_DETAILS: { event: 'deal-structure-requested', category: 'deal', outcome: 'request' },

    // DDJ / Document Management
    FETCH_DEAL_REF_ID_REQUEST: { event: 'ddj-open', category: 'document', outcome: 'request' },
    FETCH_DEAL_REF_ID_SUCCESS: { event: 'ddj-open', category: 'document', outcome: 'success' },
    FETCH_DEAL_REF_ID_FAILURE: { event: 'ddj-open', category: 'document', outcome: 'failure' },

    // Role Assumption
    ROLE_ASSUMPTION_SUCCESS: { event: 'role-assumption', category: 'auth', outcome: 'success' },
    ROLE_ASSUMPTION_FAILURE: { event: 'role-assumption', category: 'auth', outcome: 'failure' },

    // PDF Generation
    GET_PDF_URL_REQUEST: { event: 'pdf-generation', category: 'document', outcome: 'request' },
    GET_PDF_URL_SUCCESS: { event: 'pdf-generation', category: 'document', outcome: 'success' },
    GET_PDF_URL_FAILURE: { event: 'pdf-generation', category: 'document', outcome: 'failure' }
};

let previousRoute = '';
let journeyStartTime = 0;
let lastActionTime = 0;

const getDealerAttributes = (state: any) => ({
    dealXgId: state.salesCheckin?.activeDeal?.details?.dealId ?? '',
    commonOrgId: state.dealer?.commonOrgId ?? '',
    dealerMMDId: state.dealer?.dealerMMDId ?? '',
    dealerName: state.dealer?.dealerName ?? '',
    bridgeUserId: state.bridgeUser?.id ?? ''
});

const getCustomerAttributes = (state: any) => ({
    consumerId: state.salesCheckin?.activeDeal?.customer?.consumerId ?? '',
    customerType: state.salesCheckin?.activeDeal?.customer?.customerType ?? 'individual'
});

const getVehicleAttributes = (state: any) => ({
    vin: state.salesCheckin?.activeDeal?.vehicle?.vin ?? '',
    year: state.salesCheckin?.activeDeal?.vehicle?.year ?? '',
    make: state.salesCheckin?.activeDeal?.vehicle?.make ?? '',
    model: state.salesCheckin?.activeDeal?.vehicle?.model ?? '',
    stockNumber: state.salesCheckin?.activeDeal?.vehicle?.stockNumber ?? ''
});

const getMilestoneAttributes = (action: AnyFSA): Record<string, unknown> =>
    action.type === Actions.CREDIT_CONSENT_CLICKED
        ? { consentChecked: action.payload?.checked, participantRole: action.payload?.role }
        : {};

const getMilestone = (actionType: string, state: any): string | undefined => {
    if (actionType === CHANGE_VEHICLE_CLICKED) {
        const hadExistingVehicle = !!state.salesCheckin?.activeDeal?.details?.dealStructure?.vehicles?.sellingVehicle?.vin;
        return hadExistingVehicle ? MILESTONE_MAP.CHANGE_VEHICLE_CLICKED : VEHICLE_SELECTED_MILESTONE;
    }
    if (MILESTONE_MAP[actionType]) {
        return MILESTONE_MAP[actionType];
    }
    if (actionType.includes('CHECK_IN') && actionType.includes('SUCCESS')) {
        return 'check-in-completed';
    }
    return undefined;
};

export const newRelicJourneyMiddleware = (store: ReduxStore<any>) => (next: ReduxNext) => (action: AnyFSA) => {
    const result = next(action);
    const now = Date.now();

    try {
        const state = store.getState();
        const sessionId = sessionUtils.getAppSessionId();
        const dealerAttrs = getDealerAttributes(state);

        // --- Page View Tracking ---
        if (action.type === LOCATION_CHANGE) {
            const route = action.payload?.location?.pathname ?? '';

            // Instrumenting the route change explicitly allows NR to "reset the clock" for LCP for each route change.
            // If you do not do this, loading route 1 => 2 => 3 will just keep adding up to the same LCP value.
            window.newrelic?.setCurrentRouteName(route);
            window.newrelic?.addPageAction('SC:PageView', {
                route,
                ...dealerAttrs,
                ...getCustomerAttributes(state),
                timestamp: now,
                sessionId,
                previousRoute,
                timeOnPreviousRoute: previousRoute ? now - lastActionTime : 0
            });

            previousRoute = route;
            lastActionTime = now;
        }

        // --- Journey Milestone Tracking ---
        const milestone = getMilestone(action.type, state);
        if (milestone) {
            if (milestone === 'app-loaded') {
                journeyStartTime = now;
            }

            window.newrelic?.addPageAction('SC:UserAction', {
                ...dealerAttrs,
                ...getCustomerAttributes(state),
                ...getMilestoneAttributes(action),
                milestone,
                actionType: action.type,
                timestamp: now,
                sessionId,
                journeyDurationMs: journeyStartTime ? now - journeyStartTime : 0
            });
        }

        // --- Integration Point Tracking ---
        const integrationEvent = INTEGRATION_EVENT_MAP[action.type];
        if (integrationEvent) {
            const payload: Record<string, any> = {
                ...dealerAttrs,
                ...getCustomerAttributes(state),
                event: integrationEvent.event,
                category: integrationEvent.category,
                outcome: integrationEvent.outcome,
                actionType: action.type,
                timestamp: now,
                sessionId,
                timeSinceLastAction: lastActionTime ? now - lastActionTime : 0
            };

            // Add vehicle context for vehicle-related events
            if (integrationEvent.category === 'vehicle' || integrationEvent.category === 'trade-in') {
                Object.assign(payload, getVehicleAttributes(state));
            }

            // Add share-specific context
            if (integrationEvent.category === 'share') {
                payload.linkType = action.type === 'SEND_CREDIT_APP_LINK_CDL' ? 'credit-app' : 'doc-upload';
                payload.deepLinkUrl = state.salesCheckin?.deepLinks?.creditAppUrl ?? '';
            }

            // Add inbox context
            if (integrationEvent.category === 'inbox') {
                payload.customerCardTab = action.payload?.defaultTab ?? '';
                payload.messageType = action.payload?.message ?? '';
                payload.conversationType = action.payload?.occDefaultTab ?? '';
            }

            // Add error details for failures
            if (integrationEvent.outcome === 'failure' && action.payload) {
                payload.errorMessage = action.payload.message ?? action.payload.status ?? '';
                payload.errorStatus = action.payload.status ?? action.payload.statusCode ?? '';
            }

            window.newrelic?.addPageAction('SC:Integration', payload);
            lastActionTime = now;
        }
    } catch {
        // silently fail if New Relic isn't loaded or any error occurs
    }

    return result;
};
